// No dash a phone keyboard never types: T114 (25 Sep 2026).
//
// Natanel, 25 Sep 2026: "There should be NO em dashes ever used, it looks AI." Mona's own
// messages carry none (the few in her chats are text she pasted from an AI:
// data/mona-voice/ANALYSIS.md), and a model writes them constantly. The instructions say so too
// (T114a), but an instruction is a probability and this is the guarantee. The guardrail runs
// every agent draft and every spoken segment through `withoutDashes` before its rules, and what
// it releases is the cleaned text (guardrail.ts), so nothing kadmon's agents send or say carries
// one, whichever path wrote it. A recruiter's own words are theirs: `guardHumanDraft` does not
// touch them. What the brain shows from an agent is cleaned where each reply is parsed, and
// `no-dash.test.ts` holds every model call to that (T100). What we write for a person ourselves
// is held the same way by packages/db/src/no-dash-shown.test.ts (T130, 27 Sep 2026).
//
// What replaces a dash is what a person would have typed: a hyphen in a range ("10-15 min",
// "Mon-Fri", "16:15-16:30"), nothing where it opened or closed a line, and a comma everywhere
// else ("Good, that's usually..."). A spaced hyphen (" - ") is left alone, because people type it.
//
// This file holds no dash character of its own: every one is written as an escape, so a search
// for the character finds the text that carries it and never this file.

/** Em dash, en dash, figure dash, horizontal bar: the source of a character class. */
const DASH = '[\\u2012\\u2013\\u2014\\u2015]';
const ANY_DASH = new RegExp(DASH);
const EN_DASH = '\\u2013';
/** The dashes that write a range spaced or not (figure, en), and those that pause when spaced. */
const RANGE_DASH = '[\\u2012\\u2013]';
const PAUSE_DASH = '[\\u2014\\u2015]';

const RULES: ReadonlyArray<readonly [RegExp, string]> = [
  // A spaced double hyphen is a dash typed on a keyboard without one.
  [/[ \t]+--[ \t]+/g, ', '],
  // A range between numbers, prices or times: "10-15", "9am-5pm", "80-90k", "2025-2026". I-8
  // (T238j): an en or figure dash, spaced or not, or an em dash with no space round it, and never
  // across a line; a spaced em dash between numbers ("in 2025 \u2014 30 joined") is a pause.
  [
    new RegExp(
      `(\\d(?:\\s?(?:am|pm|h|k|%))?)(?:[ \\t]*${RANGE_DASH}+[ \\t]*|${PAUSE_DASH}+)(?=[\\d\\u20ac$\\u00a3])`,
      'giu',
    ),
    '$1-',
  ],
  // An unspaced en dash between two words is a range or a link: "Mon-Fri", "Stockholm-Tel Aviv".
  [new RegExp(`([\\p{L}\\p{N}])${EN_DASH}(?=[\\p{L}\\p{N}])`, 'gu'), '$1-'],
  // A dash that opens a line (a list marker, a signature) goes, keeping the indentation.
  [new RegExp(`^([ \\t]*)${DASH}+[ \\t]*`, 'gmu'), '$1'],
  // A dash that ends a line goes, with the space before it.
  [new RegExp(`[ \\t]*${DASH}+[ \\t]*$`, 'gmu'), ''],
  // A dash just before punctuation goes: "fine, ." would otherwise follow.
  [new RegExp(`[ \\t]*${DASH}+[ \\t]*(?=[,.;:!?)])`, 'gu'), ''],
  // Every other dash is a pause: a comma and a space.
  [new RegExp(`[ \\t]*${DASH}+[ \\t]*`, 'gu'), ', '],
];

/**
 * `text` with every em dash, en dash, figure dash and horizontal bar replaced by what a person
 * would have typed there. Idempotent; text without one comes back unchanged, the same string.
 */
export function withoutDashes(text: string): string {
  if (!hasDash(text)) return text;
  let out = text;
  for (const [pattern, replacement] of RULES) out = out.replace(pattern, replacement);
  // What the comma may have doubled, or left in front of a full stop.
  return out.replace(/,([ \t]*,)+/g, ',').replace(/,[ \t]+(?=[.!?])/g, '');
}

/**
 * T218 (2 Oct 2026): `change` run on the words of `text` outside its “…” quotes, each quote kept
 * character for character (a text holding the placeholders' own private-use marks is changed
 * whole). Natanel, of the WhatsApp confirm: what it quotes is "exactly as
 * written", dashes and all, and kadmon's own words around it stay dash-free. Quotes pair as they
 * nest; a quote left open runs to the end (a Doc's text continuing on the lines below), and a
 * closing mark with none open is an ordinary character (an inch mark). A stray closing mark inside
 * a quote still ends it early: where a value ends is then a guess. `keep` is what each quote
 * becomes (the WhatsApp confirm marks it `verbatim`).
 */
export function outsideQuotes(
  text: string,
  change: (part: string) => string,
  keep: (quote: string) => string = (quote) => quote,
): string {
  // Each quote is held as a placeholder while `change` runs once on the whole text, so a rule
  // that reads what follows (a dash before a quote is a pause, not a line's end) reads a word.
  if (/[\uE000\uE001]/u.test(text)) return change(text);
  const quotes: string[] = [];
  let masked = '';
  let depth = 0;
  let from = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '“') {
      if (depth === 0) {
        masked += text.slice(from, i);
        from = i;
      }
      depth += 1;
    } else if (ch === '”' && depth > 0) {
      depth -= 1;
      if (depth === 0) {
        quotes.push(text.slice(from, i + 1));
        masked += `\uE000${quotes.length - 1}\uE001`;
        from = i + 1;
      }
    }
  }
  if (depth > 0) {
    quotes.push(text.slice(from));
    masked += `\uE000${quotes.length - 1}\uE001`;
  } else {
    masked += text.slice(from);
  }
  return change(masked).replace(/\uE000(\d+)\uE001/gu, (_, n: string) => keep(quotes[Number(n)]!));
}

/** T218: the marks round what the WhatsApp confirm quotes, kept as written by its send. */
const KEPT = /[\uE010\uE011]/u;
const KEPT_ALL = /[\uE010\uE011]/gu;

/**
 * T218 (2 Oct 2026, Natanel: "Redesign: only the confirm's quotes kept"): `text` marked to be sent
 * exactly as written, the dashes and capitals in it included, by the staff reply's guard
 * (`outsideVerbatim`). Only the confirm's renderer marks anything; a mark of the text's own goes.
 */
export const verbatim = (text: string): string => `\uE010${text.replace(KEPT_ALL, '')}\uE011`;

/** `text` with no `verbatim` mark: what a model or a person writes can never claim one. */
export const withoutVerbatim = (text: string): string => text.replace(KEPT_ALL, '');

/**
 * `change` run on `text` outside its `verbatim` spans, each span sent as written without its
 * marks. A text whose marks do not pair, or that holds `outsideQuotes`' own placeholders, is changed
 * whole with its marks taken out, as before T218.
 */
export function outsideVerbatim(text: string, change: (part: string) => string): string {
  if (!KEPT.test(text)) return change(text);
  if (/[\uE000\uE001]/u.test(text)) return change(withoutVerbatim(text));
  const kept: string[] = [];
  const masked = text.replace(/\uE010([^\uE010\uE011]*)\uE011/gu, (_, inner: string) => {
    kept.push(inner);
    return `\uE000${kept.length - 1}\uE001`;
  });
  if (KEPT.test(masked)) return change(withoutVerbatim(text));
  return change(masked).replace(/\uE000(\d+)\uE001/gu, (_, n: string) => kept[Number(n)]!);
}

/**
 * T114a (25 Sep 2026): the firm's name as it is written wherever a person reads it, in lower case
 * (docs/decisions.md, the company is kadmon). Both agents are told so and a model still opens
 * a sentence with it capitalised (a client eval's reply the day it landed), so the guardrail
 * writes it the house way, as it takes out dashes. This file never spells the capital itself:
 * `company-name.test.ts` holds every living file to the lower case, and its tests build it.
 */
export function lowerCaseKadmon(text: string): string {
  return text.replace(/\bKADMON\b|\bKadmon\b/g, 'kadmon');
}

/**
 * T165 (29 Sep 2026): kadmon's people are headhunters. Natanel, asked how kadmon should say a
 * person will pick something up: "a 'kadmon headhunter' or 'check with the team' or something along
 * these lines... NOT recruiter. We are headhunters." Mona never writes the word (0 times in 901
 * working WhatsApp messages; data/mona-voice/ANALYSIS.md), and the day it was said kadmon wrote it
 * in 25 of every 100 messages. Both agents are told so; an instruction is a probability and this is
 * the guarantee: the guardrail writes the noun the house way in everything an agent sends a
 * candidate or a client, as it takes out dashes. English and Swedish, the languages the fixed lines
 * come in. What a headhunter does ("recruiting", "rekrytering") is left alone, and so is the case.
 */
const RECRUITER = /\brecruiter(s)?\b/giu;
const REKRYTERARE = /\brekryterar(e|en|na|es|ens|nas)\b/giu;
const SWEDISH: Readonly<Record<string, string>> = {
  e: 'headhunter',
  en: 'headhuntern',
  na: 'headhuntrarna',
  es: 'headhunters',
  ens: 'headhunterns',
  nas: 'headhuntrarnas',
};

/**
 * I-5 (T238j): every "recruiter" is written the house way, with no exception: Natanel, review
 * round 3, "kadmon must refer to itself as an AI headhunter and to our team as headhunters ... i
 * dont care about what people outside the team are called". A Swedish plural or genitive takes
 * its own form: "rekryterare" after a plural word ("två", "våra") is several.
 */
const PLURAL_BEFORE =
  /\b(?:två|tre|fyra|fem|sex|sju|åtta|nio|tio|flera|fler|många|några|alla|våra|era|mina|dina|sina|dessa|de|inga)\s+(?:\p{L}+\s+)?$/iu;

/** `replacement` in the case `original` was written in: all capitals, a capital, or none. */
function inCaseOf(original: string, replacement: string): string {
  if (original.length > 1 && original === original.toUpperCase()) return replacement.toUpperCase();
  if (original[0] !== original[0]!.toLowerCase()) {
    return replacement[0]!.toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

/** `text` with "recruiter" written as "headhunter", in English and Swedish. Idempotent. */
export function headhunterNotRecruiter(text: string): string {
  if (!/recruiter|rekryterar/i.test(text)) return text;
  return text
    .replace(RECRUITER, (word, plural: string | undefined) =>
      inCaseOf(word, plural ? 'headhunters' : 'headhunter'),
    )
    .replace(REKRYTERARE, (word, ending: string, at: number, whole: string) => {
      const form = ending.toLowerCase();
      const plural = form === 'e' && PLURAL_BEFORE.test(whole.slice(0, at));
      return inCaseOf(word, plural ? 'headhuntrar' : (SWEDISH[form] ?? 'headhunter'));
    });
}

/** True when `text` carries a dash `withoutDashes` would remove: what no send may ever hold. */
export function hasDash(text: string): boolean {
  return ANY_DASH.test(text) || /[ \t]--[ \t]/.test(text);
}

/**
 * T114a (25 Sep 2026): the emoji taken out of text that will be SPOKEN. Her instructions ask for
 * emoji where they fit and none on a call, but a voice that reads "slightly smiling face" aloud is
 * the kind of mistake an instruction alone should not be trusted with, so the guardrail takes them
 * out of every call segment the way it takes out dashes (guardrail.ts). Pictographs, their skin
 * tones, the variation selector and the joiner go; the space they leave is tidied.
 */
// I-8 (T238j): every pictograph Unicode names ("\u2122", "\u203C" among them) and a keycap's
// enclosing mark, so a spoken "1\uFE0F\u20E3" is read as the digit.
const PICTOGRAPH =
  '(?:[\\u{1F000}-\\u{1FAFF}\\u{2600}-\\u{27BF}\\u{2300}-\\u{23FF}\\u{2B00}-\\u{2BFF}]|\\p{Extended_Pictographic})';
const HAS_EMOJI = new RegExp(`${PICTOGRAPH}|\\u{20E3}`, 'u');
// A pictograph, or what joins, varies or encloses one: those three written outside the class,
// where a combining mark cannot pair with its neighbour (eslint no-misleading-character-class).
const EMOJI_SOURCE = `${PICTOGRAPH}|\\u{FE0F}|\\u{200D}|\\u{20E3}`;
const EMOJI = new RegExp(EMOJI_SOURCE, 'gu');

/** True when `text` carries an emoji: what the eval run's tally counts (voice.ts). */
export function hasEmoji(text: string): boolean {
  return HAS_EMOJI.test(text);
}

/** `text` with every emoji removed and the spaces they leave tidied. Idempotent. */
export function withoutEmoji(text: string): string {
  if (!HAS_EMOJI.test(text)) return text;
  // A spoken segment ends on the space before the next one: that is kept, the inner spaces tidied.
  const lead = /^\s*/.exec(text)![0];
  const trail = /\s*$/.exec(text)![0];
  const core = text.slice(lead.length, text.length - trail.length);
  const inner = closed(sentenceBreaks(core, EMOJI_SOURCE).replace(EMOJI, DROPPED)).trim();
  return inner ? lead + inner + trail : '';
}

/**
 * T146: where an emoji taken out stood between two sentences, a full stop. She often lets an emoji
 * end a sentence ("Sorry, I couldn't make out that voice note 🙈 Could you send it again?"), and
 * taking it out without one ran the two together. A word, then the emoji, then a capital: that is
 * a sentence ending. Before a lower-case word it was a pause, and a space is enough.
 */
function sentenceBreaks(text: string, removed: string): string {
  return text.replace(
    new RegExp(`([\\p{L}\\p{N})"'\\u2019])[ \\t]*(?:${removed})+[ \\t]+(?=\\p{Lu})`, 'gu'),
    '$1. ',
  );
}

/**
 * Where an emoji was taken out (`DROPPED`), the gap closed, and nothing else in the text touched
 * (I-8, T238j: a whole reply was tidied, so "Thanks :)" lost its space, and French "\u00c7a va ?"
 * too): at a line's start nothing, before punctuation or a line's end nothing, elsewhere one space.
 */
function closed(text: string): string {
  return text
    .replace(new RegExp(`^([ \\t]*)(?:${DROPPED_SOURCE}[ \\t]*)+`, 'gmu'), '$1')
    .replace(new RegExp(`[ \\t]*(?:${DROPPED_SOURCE}[ \\t]*)+(?=[.,!?;:]|$)`, 'gmu'), '')
    .replace(new RegExp(`[ \\t]*(?:${DROPPED_SOURCE}[ \\t]*)+`, 'gu'), ' ');
}

/**
 * T146 (27 Sep 2026): each emoji once in a reply. Natanel, the day a candidate's first text went
 * out with three of the same smiley in it: the fixed intro carried one, the line that thanked him
 * for his code another, and the model's words a third, each piece fine on its own. Mona repeats an
 * emoji inside one message in 10 of the 485 WhatsApp messages where she uses one, all laughter or
 * a toast among friends, never a smiley, and the people she texts repeat theirs four times as often
 * as she does (data/mona-voice/ANALYSIS.md). So a written reply keeps each emoji where it first
 * appears and loses it wherever it comes again, across every message the reply goes out as, the
 * way the guardrail takes out dashes. A skin tone or a variation selector does not make another
 * emoji of it. A message left empty by it is dropped. Text with no repeat comes back unchanged, the
 * same string. Idempotent.
 */
export function withoutRepeatedEmoji(text: string): string {
  if (!HAS_EMOJI.test(text)) return text;
  const seen = new Set<string>();
  const kept: string[] = [];
  let repeated = false;
  for (const { segment } of graphemes().segment(text)) {
    if (HAS_EMOJI.test(segment)) {
      const same = segment.replace(TONE_OR_SELECTOR, '');
      if (seen.has(same)) {
        repeated = true;
        // Marked, not dropped yet: where it ended a sentence, the sentence still ends.
        kept.push(DROPPED);
        continue;
      }
      seen.add(same);
    }
    kept.push(segment);
  }
  if (!repeated) return text;
  // Each message is tidied on its own; one that was nothing but the repeated emoji is gone.
  return closed(sentenceBreaks(kept.join(''), DROPPED_SOURCE))
    .split(/\n[ \t]*\n/)
    .map((message) => message.trim())
    .filter((message) => message.length > 0)
    .join('\n\n');
}

// Made on first use, never at import: the brain loads this file in the browser (`saidLine`), and a
// browser without `Intl.Segmenter` must still load it for the one function it calls.
let segmenter: Intl.Segmenter | null = null;
const graphemes = (): Intl.Segmenter =>
  (segmenter ??= new Intl.Segmenter('en', { granularity: 'grapheme' }));
// A skin tone or the variation selector: what makes one emoji look like another. The selector is
// written outside the class, where it cannot combine (eslint no-misleading-character-class).
const TONE_OR_SELECTOR = new RegExp('[\\u{1F3FB}-\\u{1F3FF}]|\\u{FE0F}', 'gu');
// Where a repeated emoji stood, until the sentence around it is mended: a private-use character,
// which nothing a person or a model writes carries.
const DROPPED_SOURCE = '\\u{E000}';
const DROPPED = String.fromCodePoint(0xe000);
